// MOCK ONLY. Real sampler is server-side. Do not extend, document, or reuse this file.
//
// Purpose: produce a batch of candidate inputs for the mock campaign router.
// Strategy: initial Latin-Hypercube design, then greedy next-batch selection
// using the trivial surrogate in ./surrogate.ts.
//
// score: sigma * (0.1 + |mu|/max|mu|)
//
// Deliberately weak. Do not optimise.

import type {
  AxisSpec,
  OutputSpec,
  PostDivergenceSamplingPolicy,
  SamplePoint,
  SamplerSymmetryPolicy,
} from "../types";
import { fit, predict, type TrainedSurrogate } from "./surrogate";

// ---------------------------------------------------------------------------
// Deterministic PRNG: mulberry32. Seeded from campaign id so runs are
// reproducible. Simple, stateless enough for mock usage.
// ---------------------------------------------------------------------------

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function makeRng(campaignId: string, salt = "batch") {
  return mulberry32(hashSeed(`${campaignId}:${salt}`));
}

// ---------------------------------------------------------------------------
// Axis helpers
// ---------------------------------------------------------------------------

export function rangeAxes(inputs: AxisSpec[]): AxisSpec[] {
  return inputs.filter((a) => a.mode === "range");
}

export function fixedValues(inputs: AxisSpec[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const a of inputs) {
    if (a.mode === "fixed" && a.fixedValue != null) {
      out[a.name] = a.fixedValue;
    }
  }
  return out;
}

function symmetryAxis(
  inputs: AxisSpec[],
  policy: SamplerSymmetryPolicy | undefined,
): AxisSpec | null {
  if (!policy?.enabled || !policy.axisName) return null;
  const axis = inputs.find((item) => item.name === policy.axisName && item.mode === "range");
  return axis ?? null;
}

type SymmetryPolicies = SamplerSymmetryPolicy | readonly SamplerSymmetryPolicy[] | undefined;

function activeSymmetryPolicies(policies: SymmetryPolicies): SamplerSymmetryPolicy[] {
  const items = Array.isArray(policies) ? policies : policies ? [policies] : [];
  const byAxis = new Map<string, SamplerSymmetryPolicy>();
  for (const policy of items) {
    if (policy.enabled && policy.axisName) byAxis.set(policy.axisName, policy);
  }
  return [...byAxis.values()];
}

function symmetryCentre(axis: AxisSpec, policy: SamplerSymmetryPolicy): number {
  const lo = axis.min ?? 0;
  const hi = axis.max ?? 1;
  const centre = policy.centre ?? 0;
  return Math.max(lo, Math.min(hi, centre));
}

export function applySymmetrySamplingInputs(
  inputs: AxisSpec[],
  policies: SymmetryPolicies,
): AxisSpec[] {
  return activeSymmetryPolicies(policies)
    .filter((policy) => policy.sampleHalf)
    .reduce((currentInputs, policy) => {
      const axis = symmetryAxis(currentInputs, policy);
      if (!axis) return currentInputs;
      const centre = symmetryCentre(axis, policy);
      const side = policy.sampleSide ?? "positive";
      return currentInputs.map((item) => {
        if (item.name !== axis.name || item.mode !== "range") return item;
        return side === "negative"
          ? { ...item, max: centre }
          : { ...item, min: centre };
      });
    }, inputs);
}

export function isAllowedBySymmetrySampling(
  point: Record<string, number>,
  inputs: AxisSpec[],
  policies: SymmetryPolicies,
): boolean {
  return activeSymmetryPolicies(policies)
    .filter((policy) => policy.sampleHalf)
    .every((policy) => {
      const axis = symmetryAxis(inputs, policy);
      if (!axis) return true;
      const value = point[axis.name];
      if (value == null || !Number.isFinite(value)) return true;
      const centre = symmetryCentre(axis, policy);
      return (policy.sampleSide ?? "positive") === "negative"
        ? value <= centre + 1e-9
        : value >= centre - 1e-9;
    });
}

function outputSymmetrySign(outputName: string, axisName: string): number {
  if (axisName !== "beta") return 1;
  return outputName === "CY" || outputName === "Cmx" || outputName === "Cmz" ? -1 : 1;
}

function mirrorOutputRecord(
  source: Record<string, number> | undefined,
  outputs: OutputSpec[],
  axisName: string,
): Record<string, number> | undefined {
  if (!source) return undefined;
  const configured = new Set(outputs.map((output) => output.name));
  const mirrored: Record<string, number> = {};
  for (const [name, value] of Object.entries(source)) {
    if (!Number.isFinite(value)) continue;
    const sign = configured.has(name) ? outputSymmetrySign(name, axisName) : 1;
    mirrored[name] = value * sign;
  }
  return mirrored;
}

function mirrorPredictedRecord(
  source: SamplePoint["predicted"],
  outputs: OutputSpec[],
  axisName: string,
): SamplePoint["predicted"] {
  if (!source) return undefined;
  const configured = new Set(outputs.map((output) => output.name));
  const mirrored: NonNullable<SamplePoint["predicted"]> = {};
  for (const [name, value] of Object.entries(source)) {
    if (!value) continue;
    const sign = configured.has(name) ? outputSymmetrySign(name, axisName) : 1;
    mirrored[name] = { mean: value.mean * sign, sigma: value.sigma };
  }
  return mirrored;
}

export function symmetryAugmentedObservations(
  inputs: AxisSpec[],
  outputs: OutputSpec[],
  observed: SamplePoint[],
  policies: SymmetryPolicies,
): SamplePoint[] {
  let augmented = [...observed];
  const seenIds = new Set(augmented.map((point) => point.id));
  for (const policy of activeSymmetryPolicies(policies).filter((item) => item.mirrorObservations)) {
    const axis = symmetryAxis(inputs, policy);
    if (!axis) continue;
    const centre = symmetryCentre(axis, policy);
    const lo = axis.min ?? -Infinity;
    const hi = axis.max ?? Infinity;
    const source = [...augmented];
    for (const point of source) {
      const value = point.inputs[axis.name];
      if (value == null || !Number.isFinite(value)) continue;
      const mirroredValue = 2 * centre - value;
      if (Math.abs(mirroredValue - value) < 1e-9) continue;
      if (mirroredValue < lo - 1e-9 || mirroredValue > hi + 1e-9) continue;
      const id = `${point.id}__sym_${axis.name}`;
      if (seenIds.has(id)) continue;
      seenIds.add(id);
      augmented.push({
        ...point,
        id,
        inputs: {
          ...point.inputs,
          [axis.name]: mirroredValue,
          ...(axis.name === "beta" ? Object.fromEntries(
            ["da", "dr", "P", "R"]
              .filter((name) => inputs.some((input) => input.name === name) && Number.isFinite(point.inputs[name]))
              .map((name) => [name, -(point.inputs[name] ?? 0)]),
          ) : {}),
        },
        outputs: mirrorOutputRecord(point.outputs, outputs, axis.name),
        truthOutputs: mirrorOutputRecord(point.truthOutputs, outputs, axis.name),
        predicted: mirrorPredictedRecord(point.predicted, outputs, axis.name),
      });
    }
  }
  return augmented;
}

function fillFixed(
  rangeVals: Record<string, number>,
  inputs: AxisSpec[],
): Record<string, number> {
  return { ...fixedValues(inputs), ...rangeVals };
}

// ---------------------------------------------------------------------------
// Initial Latin-Hypercube design over the range-mode axes.
// ---------------------------------------------------------------------------

export function initialDesign(
  inputs: AxisSpec[],
  rng: () => number,
  sampleCount?: number,
): Array<Record<string, number>> {
  const range = rangeAxes(inputs);
  if (range.length === 0) return [];
  const nSamples = Math.max(1, Math.trunc(sampleCount ?? 2 * range.length));

  // For each axis, build a shuffled list of stratified bin centres.
  const perAxis = range.map((axis) => {
    const lo = axis.min ?? 0;
    const hi = axis.max ?? 1;
    const bins: number[] = [];
    for (let i = 0; i < nSamples; i++) {
      const u = (i + rng()) / nSamples;
      bins.push(lo + u * (hi - lo));
    }
    // Fisher-Yates shuffle.
    for (let i = bins.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const a = bins[i] ?? 0;
      const b = bins[j] ?? 0;
      bins[i] = b;
      bins[j] = a;
    }
    return bins;
  });

  const samples: Array<Record<string, number>> = [];
  for (let k = 0; k < nSamples; k++) {
    const point: Record<string, number> = {};
    range.forEach((axis, idx) => {
      const col = perAxis[idx] ?? [];
      point[axis.name] = col[k] ?? 0;
    });
    samples.push(fillFixed(point, inputs));
  }
  return samples;
}

// ---------------------------------------------------------------------------
// Greedy batch selection.
// ---------------------------------------------------------------------------

function normalisePoint(
  point: Record<string, number>,
  range: AxisSpec[],
): number[] {
  return range.map((axis) => {
    const lo = axis.min ?? 0;
    const hi = axis.max ?? 1;
    const span = hi - lo || 1;
    const v = point[axis.name] ?? 0;
    return (v - lo) / span;
  });
}

function candidateGrid(
  range: AxisSpec[],
  rng: () => number,
  count = 400,
): Array<Record<string, number>> {
  const out: Array<Record<string, number>> = [];
  for (let i = 0; i < count; i++) {
    const pt: Record<string, number> = {};
    for (const axis of range) {
      const lo = axis.min ?? 0;
      const hi = axis.max ?? 1;
      pt[axis.name] = lo + rng() * (hi - lo);
    }
    out.push(pt);
  }
  return out;
}

type PerOutputModel = { name: string; model: TrainedSurrogate; weight: number };

type DivergenceFrontier = {
  axisName: string;
  outputName: string;
  limit: number;
};

const DEFAULT_DIVERGENCE_MIN_POINTS = 6;
const DEFAULT_DIVERGENCE_DROP_FRACTION = 0.18;

function pickDivergenceAxis(
  inputs: AxisSpec[],
  policy?: PostDivergenceSamplingPolicy,
): AxisSpec | null {
  const range = rangeAxes(inputs);
  if (range.length === 0) return null;
  if (policy?.axisName) {
    const explicit = range.find((axis) => axis.name === policy.axisName);
    if (explicit) return explicit;
  }
  return range.find((axis) => axis.name === "alpha") ?? range[0] ?? null;
}

function pickDivergenceOutput(
  outputs: OutputSpec[],
  policy?: PostDivergenceSamplingPolicy,
): string | null {
  if (outputs.length === 0) return null;
  if (policy?.outputName && outputs.some((o) => o.name === policy.outputName)) {
    return policy.outputName;
  }
  return outputs.some((o) => o.name === "CL") ? "CL" : (outputs[0]?.name ?? null);
}

export function inferDivergenceFrontier(
  inputs: AxisSpec[],
  outputs: OutputSpec[],
  observed: SamplePoint[],
  policy?: PostDivergenceSamplingPolicy,
): DivergenceFrontier | null {
  if (!policy?.enabled) return null;
  const axis = pickDivergenceAxis(inputs, policy);
  const outputName = pickDivergenceOutput(outputs, policy);
  if (!axis || !outputName) return null;

  const rows = observed
    .map((point) => ({
      x: point.inputs[axis.name],
      y: point.outputs?.[outputName],
    }))
    .filter(
      (row): row is { x: number; y: number } =>
        Number.isFinite(row.x) && Number.isFinite(row.y),
    )
    .sort((a, b) => a.x - b.x);

  const minPoints = policy.minPoints ?? DEFAULT_DIVERGENCE_MIN_POINTS;
  if (rows.length < minPoints) return null;

  let peakIdx = 0;
  for (let i = 1; i < rows.length; i++) {
    if (rows[i]!.y > rows[peakIdx]!.y) peakIdx = i;
  }
  if (peakIdx >= rows.length - 1) return null;

  const ys = rows.map((row) => row.y);
  const span = Math.max(Math.max(...ys) - Math.min(...ys), 1e-9);
  const dropThreshold =
    span * (policy.dropFraction ?? DEFAULT_DIVERGENCE_DROP_FRACTION);
  const peak = rows[peakIdx]!;

  for (let i = peakIdx + 1; i < rows.length; i++) {
    if (peak.y - rows[i]!.y >= dropThreshold) {
      const axisSpan = (axis.max ?? peak.x) - (axis.min ?? peak.x);
      const guardBand = policy.guardBand ?? Math.max(axisSpan * 0.05, 0);
      return {
        axisName: axis.name,
        outputName,
        limit: peak.x + guardBand,
      };
    }
  }

  return null;
}

export function isAllowedByDivergenceFrontier(
  point: Record<string, number>,
  frontier: DivergenceFrontier | null,
): boolean {
  if (!frontier) return true;
  const value = point[frontier.axisName];
  return value == null || !Number.isFinite(value) || value <= frontier.limit;
}

function fitAllOutputs(
  inputs: AxisSpec[],
  outputs: OutputSpec[],
  observed: SamplePoint[],
): PerOutputModel[] {
  const range = rangeAxes(inputs);
  if (observed.length === 0 || range.length === 0) {
    return outputs.map((o) => ({
      name: o.name,
      weight: o.weight,
      model: fit([], []),
    }));
  }

  const xsAll = observed.map((p) => normalisePoint(p.inputs, range));

  return outputs.map((o) => {
    const ys: number[] = [];
    const xs: number[][] = [];
    for (let i = 0; i < observed.length; i++) {
      const p = observed[i];
      const row = xsAll[i];
      if (!p || !row) continue;
      const v = p.outputs?.[o.name];
      if (v != null && Number.isFinite(v)) {
        ys.push(v);
        xs.push(row);
      }
    }
    return { name: o.name, weight: o.weight, model: fit(xs, ys) };
  });
}

function scoreAt(
  xNorm: number[],
  perOutput: PerOutputModel[],
  maxAbsMu: Record<string, number>,
  disabled: Set<string> = new Set(),
): number {
  let total = 0;
  let wSum = 0;
  for (const { name, model, weight } of perOutput) {
    if (disabled.has(name)) continue;
    const { mean, sigma } = predict(model, xNorm);
    const denom = Math.max(maxAbsMu[name] ?? 1, 1e-9);
    const contrib = sigma * (0.1 + Math.abs(mean) / denom);
    total += weight * contrib;
    wSum += weight;
  }
  return wSum > 0 ? total / wSum : 0;
}

// Approximate max-|mu| over a light random sample for score normalisation.
function computeMaxAbsMu(
  range: AxisSpec[],
  perOutput: PerOutputModel[],
  rng: () => number,
): Record<string, number> {
  const out: Record<string, number> = {};
  const samples = candidateGrid(range, rng, 50);
  for (const { name, model } of perOutput) {
    let m = 1e-6;
    for (const s of samples) {
      const x = normalisePoint(s, range);
      const { mean } = predict(model, x);
      const a = Math.abs(mean);
      if (a > m) m = a;
    }
    out[name] = m;
  }
  return out;
}

export function proposeBatch(
  campaignId: string,
  inputs: AxisSpec[],
  outputs: OutputSpec[],
  observed: SamplePoint[],
  batchSize: number,
  iteration: number,
  postDivergenceSampling?: PostDivergenceSamplingPolicy,
  symmetry?: SymmetryPolicies,
): Array<Record<string, number>> {
  const range = rangeAxes(inputs);
  if (range.length === 0) return [];

  const rng = makeRng(campaignId, `iter-${iteration}`);
  const samplingInputs = applySymmetrySamplingInputs(inputs, symmetry);
  const samplingRange = rangeAxes(samplingInputs);
  const workingObserved = symmetryAugmentedObservations(inputs, outputs, observed, symmetry);

  // Cold start: LHS initial design, optionally padded to batchSize.
  if (workingObserved.length === 0) {
    const seed = initialDesign(samplingInputs, rng)
      .filter((point) => isAllowedBySymmetrySampling(point, inputs, symmetry));
    const out: Array<Record<string, number>> = [];
    if (seed.length === 0) return out;
    for (let i = 0; i < batchSize; i++) {
      const s = seed[i % seed.length];
      if (s) out.push(s);
    }
    return out.slice(0, batchSize);
  }

  // Fit per-output surrogates on observations.
  let perOutput = fitAllOutputs(inputs, outputs, workingObserved);
  let maxAbsMu = computeMaxAbsMu(range, perOutput, rng);
  const divergenceFrontier = inferDivergenceFrontier(
    inputs,
    outputs,
    observed,
    postDivergenceSampling,
  );

  const picks: Array<Record<string, number>> = [];
  const hallucinated: SamplePoint[] = [];

  for (let b = 0; b < batchSize; b++) {
    const candidates = candidateGrid(samplingRange, rng, 400);
    let bestScore = -Infinity;
    let bestPoint: Record<string, number> | null = null;
    let bestX: number[] | null = null;
    for (const cand of candidates) {
      const candidatePoint = fillFixed(cand, inputs);
      if (!isAllowedByDivergenceFrontier(candidatePoint, divergenceFrontier)) {
        continue;
      }
      if (!isAllowedBySymmetrySampling(candidatePoint, inputs, symmetry)) {
        continue;
      }
      const xn = normalisePoint(cand, range);
      const s = scoreAt(xn, perOutput, maxAbsMu);
      if (s > bestScore) {
        bestScore = s;
        bestPoint = candidatePoint;
        bestX = xn;
      }
    }
    if (!bestPoint || !bestX) break;
    picks.push(bestPoint);

    // Hallucinate observation using current surrogate means and refit so the
    // next pick diversifies.
    const hallOutputs: Record<string, number> = {};
    for (const { name, model } of perOutput) {
      hallOutputs[name] = predict(model, bestX).mean;
    }
    const stub: SamplePoint = {
      id: `__hallucinated_${b}`,
      iter: iteration,
      batchId: "__stub__",
      status: "done",
      source: "sampler",
      inputs: bestPoint,
      outputs: hallOutputs,
    };
    hallucinated.push(stub);
    perOutput = fitAllOutputs(inputs, outputs, symmetryAugmentedObservations(inputs, outputs, [...observed, ...hallucinated], symmetry));
    maxAbsMu = computeMaxAbsMu(range, perOutput, rng);
  }

  return picks;
}

// Export the scoring helper and surrogate fitter for the router's surface /
// score endpoints — keeps all the mock-only vocabulary confined to this module
// tree.
export { fitAllOutputs, scoreAt, computeMaxAbsMu, normalisePoint };
export type { PerOutputModel };
